import { Capacitor } from '@capacitor/core';
import type { LineItem } from '@/models/cartModels';
import type { MenuItem } from '@/models/menuModels';
import { getBrazeFeatureConfig } from '@/services/featureService';
import { trackBrazeEvent } from './useBraze';

/**
 * Braze eCommerce recommended events logged from the app: product_viewed,
 * cart_updated and checkout_started. ecommerce.order_placed is sent by the API
 * on a successful /submitorder so guest and member orders are both covered and
 * counted once.
 *
 * Braze validates these against a strict schema and silently drops failures:
 * custom data goes in metadata (never top level), money must be >= 0, and each
 * product needs product_id, product_name, variant_id, quantity and price.
 * https://www.braze.com/docs/user_guide/data/activation/events/recommended_events
 *
 * The product/variant id scheme must match the API's BrazeEcommerceMapper.
 */

const CURRENCY = 'USD';
const CART_ID_KEY = 'braze_cart_id';
const CART_UPDATE_DEBOUNCE_MS = 1000;

export interface EcommerceProduct {
    product_id: string;
    product_name: string;
    variant_id: string;
    quantity: number;
    price: number;
    image_url?: string;
    metadata?: Record<string, unknown>;
}

export interface CartTotals {
    subtotal: number;
    tax: number;
    shipping: number;
    total: number;
}

const money = (value: unknown): number => {
    const n = Math.abs(Number(value));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

const stripTags = (value: string): string => value.replace(/<[^>]*>/g, '');

const slug = (value: string): string =>
    stripTags(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const withoutEmpty = (obj: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

const imageUrl = (path?: string): string | undefined => {
    if (!path) return undefined;
    if (/^https?:\/\//.test(path)) return path;
    const base = process.env.VUE_APP_IMAGE_URL;
    return base ? `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}` : undefined;
};

const newId = (): string => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

export const brazeSource = (): string => Capacitor.getPlatform(); // web | ios | android

/** "<cat>-<item>" from ids like "store=65,cat=3,webcat=3,item=20" => "3-20". */
export const brazeProductId = (id: string | undefined, name: string): string => {
    const item = id?.match(/(?:^|,)item=([^,]+)/)?.[1];
    if (item) {
        const cat = id?.match(/(?:^|,)cat=([^,]+)/)?.[1];
        return cat ? `${cat}-${item}` : item;
    }
    return id || slug(name) || 'unknown';
};

export const brazeVariantId = (productId: string, size?: string, crust?: string): string => [productId, slug(size ?? ''), slug(crust ?? '')].filter((v) => v !== '').join('_');

export const toEcommerceProduct = (item: LineItem): EcommerceProduct => {
    const name = stripTags(item.displayName || item.name || '').trim() || 'Unknown Item';
    const productId = brazeProductId(item.id, name);
    const size = item.firstSize?.name ?? '';
    const crust = item.firstRecipe?.base ?? '';
    const isHalfAndHalf = !!item.name2;

    const product: EcommerceProduct = {
        product_id: productId,
        product_name: name,
        variant_id: brazeVariantId(productId, size, crust),
        quantity: Math.max(0, Math.trunc(+item.quantity || 0)),
        // LineItem.price is the unit price; the cart subtotal is quantity * price
        price: money(item.price),
    };

    const image = imageUrl(item.imageSm);
    if (image) product.image_url = image;

    const metadata = withoutEmpty({
        category: item.category,
        size,
        crust,
        is_half_and_half: isHalfAndHalf || undefined,
        second_half: isHalfAndHalf ? stripTags(item.name2 ?? '') : undefined,
    });
    if (Object.keys(metadata).length) product.metadata = metadata;

    return product;
};

// #region cart id
let memoryCartId: string | null = null;

const readCartId = (): string | null => {
    try {
        return localStorage.getItem(CART_ID_KEY) ?? memoryCartId;
    } catch {
        return memoryCartId;
    }
};

export const getBrazeCartId = (): string => {
    const existing = readCartId();
    if (existing) return existing;
    const id = newId();
    memoryCartId = id;
    try {
        localStorage.setItem(CART_ID_KEY, id);
    } catch {
        // storage unavailable: memoryCartId keeps this cart's id stable instead
    }
    return id;
};

export const resetBrazeCartId = (): void => {
    clearTimeout(cartUpdateTimer);
    memoryCartId = null;
    try {
        localStorage.removeItem(CART_ID_KEY);
    } catch {
        // nothing to clear
    }
};
// #endregion

const isEnabled = async (): Promise<boolean> => (await getBrazeFeatureConfig()).ecommerceEnabled;

// Properties are built lazily inside the try so nothing here can throw into
// the cart or checkout code that calls these trackers.
const log = async (name: string, buildProperties: () => Record<string, unknown>): Promise<void> => {
    try {
        if (!(await isEnabled())) return;
        await trackBrazeEvent(name, buildProperties());
    } catch (error) {
        console.log('Braze eCommerce Error', error ? ` - ${error}` : '');
    }
};

export const trackProductViewed = (menuItem: MenuItem, lineItem: LineItem): Promise<void> =>
    log('ecommerce.product_viewed', () => {
        const product = toEcommerceProduct(lineItem);
        const baseUrl = process.env.VUE_APP_BASE_URL;
        return {
            product_id: product.product_id,
            product_name: product.product_name,
            variant_id: product.variant_id,
            price: product.price,
            currency: CURRENCY,
            source: brazeSource(),
            ...(product.image_url ? { image_url: product.image_url } : {}),
            ...(baseUrl && menuItem.fullPath ? { product_url: `${baseUrl.replace(/\/+$/, '')}${menuItem.fullPath}` } : {}),
            ...(product.metadata ? { metadata: product.metadata } : {}),
        };
    });

/** Cart totals from the order store's getters. */
export const cartTotalsFromGetters = (getters: any): CartTotals => ({
    // post-discount, pre-tax/shipping, as Braze defines subtotal_value
    subtotal: (getters.getSubTotal ?? 0) - (getters.getCouponsDiscount ?? 0) - (getters.getAppliedRewardDiscount ?? 0),
    tax: getters.getTax ?? 0,
    shipping: getters.getDeliveryCharge ?? 0,
    total: getters.getGrandTotal ?? 0,
});

const cartProperties = (items: ReadonlyArray<LineItem>, totals: CartTotals) => ({
    total_value: money(totals.total),
    subtotal_value: money(totals.subtotal),
    tax: money(totals.tax),
    shipping: money(totals.shipping),
    currency: CURRENCY,
    products: items.map(toEcommerceProduct),
    source: brazeSource(),
});

let cartUpdateTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Full cart replacement (no action property): every event carries the whole
 * cart with absolute quantities. Braze warns against mixing this with
 * incremental add/remove updates for the same cart_id, so only this form is
 * used. Debounced because one user action can reprice the cart several times.
 */
export const trackCartUpdated = (items: ReadonlyArray<LineItem>, totals: CartTotals): void => {
    clearTimeout(cartUpdateTimer);
    const snapshot = { items: [...items], totals: { ...totals } };
    cartUpdateTimer = setTimeout(() => {
        log('ecommerce.cart_updated', () => ({
            cart_id: getBrazeCartId(),
            ...cartProperties(snapshot.items, snapshot.totals),
        }));
    }, CART_UPDATE_DEBOUNCE_MS);
};

export const trackCheckoutStarted = (items: ReadonlyArray<LineItem>, totals: CartTotals): Promise<void> => {
    if (!items.length) return Promise.resolve();
    return log('ecommerce.checkout_started', () => ({
        checkout_id: newId(),
        cart_id: getBrazeCartId(),
        ...cartProperties(items, totals),
    }));
};
